/**
 * User account and submission page
 * PURPOSE: Handles customer accounts, property posting and submission-status tracking.
 *
 * EDITING GUIDE:
 * - Read the comments above each section or function before changing behavior.
 * - Keep validation, authentication and error handling in place for production safety.
 * - Search for "EDIT HERE" to find the most common business-content settings.
 */

import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, assetUrl } from '../api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { PropertyMapPicker } from '../components/PropertyMap';
import { useLanguage } from '../i18n';
import './UserDashboard.css';

// PROPERTY FORM DEFAULTS: reset to these values after a successful submission.
const EMPTY_POST = {
  title: '', price: '', location: '', latitude: '', longitude: '', type: 'Vente', category: 'Appartement',
  bedrooms: '', bathrooms: '', area: '', contactPhone: '', description: '',
};

export default function UserDashboard() {
  // TRANSLATION AND URL CONTEXT: ?post=1 opens the registration-first experience.
  const { t } = useLanguage();
  const logoUrl = '/logo.jpeg?v=2';
  const [searchParams] = useSearchParams();
  // COMPONENT STATE: authentication, account form, posting form and submission history.
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState(searchParams.get('post') ? 'register' : 'login');
  const [authForm, setAuthForm] = useState({ displayName: '', email: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [postForm, setPostForm] = useState(EMPTY_POST);
  const [files, setFiles] = useState([]);
  const [postLoading, setPostLoading] = useState(false);
  const [postMessage, setPostMessage] = useState('');

  /**
   * Load posts from the backend and update the page state.
   */
  const fetchPosts = async () => {
    const response = await api.get('/api/properties/mine');
    setPosts(response.data.data || []);
  };

  // SESSION RESTORE: keep returning users signed in and load their previous submissions.
  useEffect(() => {
    api.get('/api/users/session')
      .then(async (response) => {
        setUser(response.data.user);
        await fetchPosts();
      })
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  /**
   * Handle the auth user action and keep interface state synchronized.
   */
  const handleAuth = async (event) => {
    event.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      const endpoint = mode === 'register' ? '/api/users/register' : '/api/users/login';
      const payload = mode === 'register' ? authForm : { email: authForm.email, password: authForm.password };
      const response = await api.post(endpoint, payload);
      setUser(response.data.user);
      setAuthForm({ displayName: '', email: '', password: '' });
      await fetchPosts();
    } catch (error) {
      setAuthError(error.response?.data?.message || 'Connexion impossible.');
    } finally {
      setAuthLoading(false);
    }
  };

  /**
   * End the user session and clear private dashboard data from memory.
   */
  const logout = async () => {
    await api.post('/api/users/logout').catch(() => null);
    setUser(null);
    setPosts([]);
  };

  /**
   * Submit post to the backend and refresh the affected interface.
   */
  const submitPost = async (event) => {
    event.preventDefault();
    setPostLoading(true);
    setPostMessage('');
    const data = new FormData();
    Object.entries(postForm).forEach(([key, value]) => data.append(key, value ?? ''));
    Array.from(files || []).forEach((file) => data.append('images', file));

    try {
      const response = await api.post('/api/properties/submit', data);
      setPostForm(EMPTY_POST);
      setFiles([]);
      event.target.reset();
      setPostMessage(response.data.message || t('submitSuccess'));
      await fetchPosts();
    } catch (error) {
      if (error.response?.status === 401) setUser(null);
      setPostMessage(error.response?.data?.message || 'Envoi impossible.');
    } finally {
      setPostLoading(false);
    }
  };

  if (checking) return <div className="user-page-loading">...</div>;

  if (!user) {
    // ACCOUNT ENTRY SCREEN: registration and login are shown before a valid session exists.
    return (
      <div className="user-auth-page">
        <div className="user-topbar"><Link to="/">← {t('back')}</Link><LanguageSwitcher /></div>
        <div className="user-auth-layout">
          <section className="user-auth-intro">
            <span>CAPITAL IMMOBILIÈRE</span>
            <h1>{t('accountTitle')}</h1>
            <p>{t('accountIntro')}</p>
            <div className="account-benefits"><div>✓ {t('accountRequired')}</div><div>✓ {t('myPosts')}</div><div>✓ {t('statusNote')}</div></div>
          </section>
          <form className="user-auth-card" onSubmit={handleAuth}>
            <div className="auth-tabs"><button type="button" className={mode === 'register' ? 'active' : ''} onClick={() => { setMode('register'); setAuthError(''); }}>{t('register')}</button><button type="button" className={mode === 'login' ? 'active' : ''} onClick={() => { setMode('login'); setAuthError(''); }}>{t('login')}</button></div>
            {mode === 'register' && <label><span>{t('name')}</span><input value={authForm.displayName} onChange={(event) => setAuthForm({ ...authForm, displayName: event.target.value })} minLength="2" maxLength="80" required /></label>}
            <label><span>{t('email')}</span><input type="email" value={authForm.email} onChange={(event) => setAuthForm({ ...authForm, email: event.target.value })} required /></label>
            <label><span>{t('password')}</span><input type="password" value={authForm.password} onChange={(event) => setAuthForm({ ...authForm, password: event.target.value })} minLength={mode === 'register' ? 8 : undefined} required /></label>
            {authError && <p className="user-error">{authError}</p>}
            <button className="user-primary-btn" type="submit" disabled={authLoading}>{authLoading ? '...' : mode === 'register' ? t('register') : t('login')}</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="user-dashboard">
      {/* AUTHENTICATED USER AREA: posting form followed by submission tracking. */}
      <header className="user-dashboard-header">
        <Link to="/" className="user-brand"><img src={logoUrl} alt="Capital Immobilière" /><span>CAPITAL IMMOBILIÈRE</span></Link>
        <div><LanguageSwitcher /><span className="user-name">{user.displayName}</span><button type="button" onClick={logout}>{t('logout')}</button></div>
      </header>

      <main className="user-dashboard-main">
        <section className="user-welcome"><span>{t('accountTitle')}</span><h1>{t('newPost')}</h1><p>{t('accountIntro')}</p></section>

        {/* EDIT HERE: fields shown to owners when they submit a property. */}
        {/* PROPERTY SUBMISSION FORM: new posts enter the admin moderation queue. */}
        <section className="user-post-section">
          <form className="user-post-form" onSubmit={submitPost}>
            <label><span>{t('title')}</span><input value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} maxLength="150" required /></label>
            <label><span>{t('price')}</span><input type="number" min="0" value={postForm.price} onChange={(e) => setPostForm({ ...postForm, price: e.target.value })} required /></label>
            <label><span>{t('location')}</span><input value={postForm.location} onChange={(e) => setPostForm({ ...postForm, location: e.target.value })} maxLength="200" required /></label>
            <label><span>{t('phone')}</span><input type="tel" value={postForm.contactPhone} onChange={(e) => setPostForm({ ...postForm, contactPhone: e.target.value })} maxLength="40" /></label>
            <label><span>{t('operation')}</span><select value={postForm.type} onChange={(e) => setPostForm({ ...postForm, type: e.target.value })}><option value="Vente">{t('sale')}</option><option value="Location">{t('rent')}</option></select></label>
            <label><span>{t('category')}</span><select value={postForm.category} onChange={(e) => setPostForm({ ...postForm, category: e.target.value })}>{['Appartement', 'Villa', 'Maison', 'Terrain', 'Bureau', 'Local commercial', 'Autre'].map((item) => <option key={item}>{item}</option>)}</select></label>
            <label><span>{t('bedrooms')}</span><input type="number" min="0" max="100" value={postForm.bedrooms} onChange={(e) => setPostForm({ ...postForm, bedrooms: e.target.value })} /></label>
            <label><span>{t('bathrooms')}</span><input type="number" min="0" max="100" value={postForm.bathrooms} onChange={(e) => setPostForm({ ...postForm, bathrooms: e.target.value })} /></label>
            <label><span>{t('area')}</span><input type="number" min="0" value={postForm.area} onChange={(e) => setPostForm({ ...postForm, area: e.target.value })} /></label>

            {/* PROPERTY MAP: the selected point becomes the map shown after the listing is approved. */}
            <PropertyMapPicker
              latitude={postForm.latitude}
              longitude={postForm.longitude}
              required
              onChange={({ latitude, longitude }) => setPostForm((current) => ({ ...current, latitude, longitude }))}
            />

            <label className="full"><span>{t('propertyDescription')}</span><textarea value={postForm.description} onChange={(e) => setPostForm({ ...postForm, description: e.target.value })} maxLength="5000" required /></label>
            <label className="full"><span>{t('images')} (10 max.)</span><input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={(e) => setFiles(e.target.files)} required /></label>
            {postMessage && <p className="post-message full">{postMessage}</p>}
            <button type="submit" className="user-primary-btn full" disabled={postLoading}>{postLoading ? '...' : t('submitPost')}</button>
          </form>
        </section>

        {/* SUBMISSION HISTORY: shows pending, approved or rejected status and notes. */}
        <section className="my-posts-section">
          <div className="my-posts-heading"><div><span>TRACKING</span><h2>{t('myPosts')}</h2></div><strong>{posts.length}</strong></div>
          <div className="my-posts-grid">
            {posts.length ? posts.map((post) => (
              <article key={post._id} className={`user-post-card ${post.moderationStatus || 'approved'}`}>
                <img src={assetUrl(post.images?.[0])} alt={post.title} onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }} />
                <div><span className="post-status">{t(post.moderationStatus || 'approved')}</span><h3>{post.title}</h3><p>📍 {post.location}</p><strong>{Number(post.price).toLocaleString()} DT</strong>{post.moderationNote && <p className="moderation-note"><b>{t('statusNote')}:</b> {post.moderationNote}</p>}{post.moderationStatus === 'approved' && <Link to={`/property/${post._id}`}>{t('details')} →</Link>}</div>
              </article>
            )) : <div className="empty-user-posts">{t('noResults')}</div>}
          </div>
        </section>
      </main>
    </div>
  );
}
